import React, {useEffect, useRef, useState} from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

type AudioPlayerProps = {
    src: string;
};

function formatTime(seconds: number): string {
    if (!isFinite(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
}

export function AudioPlayer({src}: AudioPlayerProps) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        setPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        audio.load();
    }, [src]);

    const toggle = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) {
            audio.pause();
        } else {
            audio.play();
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;
        const t = parseFloat(e.target.value);
        audio.currentTime = t;
        setCurrentTime(t);
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-light-light-grey bg-white">
            <audio
                ref={audioRef}
                src={src}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
                onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
            />
            <button
                type="button"
                onClick={toggle}
                className="flex items-center justify-center w-5 h-5 rounded-full border border-light-grey text-grey hover:bg-light-light-grey transition-colors shrink-0"
            >
                {playing
                    ? <PauseIcon style={{fontSize: 12}}/>
                    : <PlayArrowIcon style={{fontSize: 12}}/>
                }
            </button>
            <div className="flex-1 flex items-center gap-2 min-w-0">
                <div className="relative flex-1 h-1 bg-light-light-grey rounded-full">
                    <div
                        className="absolute inset-y-0 left-0 bg-light-grey rounded-full pointer-events-none"
                        style={{width: `${progress}%`}}
                    />
                    <input
                        type="range"
                        min={0}
                        max={duration || 0}
                        step={0.01}
                        value={currentTime}
                        onChange={handleSeek}
                        className="absolute inset-0 w-full opacity-0 cursor-pointer h-full"
                    />
                </div>
                <span className="text-xs text-light-grey tabular-nums shrink-0">
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>
            </div>
        </div>
    );
}
